import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { LuArrowLeft, LuChevronLeft, LuChevronRight, LuCheck, LuMaximize2, LuExternalLink } from "react-icons/lu";
import "../../styles/infoscription/registrationHelp.css";


//ÉTAPES DU GUIDE D'INSCRIPTION SUR LE SITE DE LA FFVB
const HELP_STEPS = [
  {
    title: "Connectez-vous à la FFVB",
    imageUrl: "/images/registrationHelp/createAnAccountOrConnect.png",
    texts: [
      "Pas encore de compte FFVB ? Cliquez sur « Créer » pour créer votre compte.",
      "Pour inscrire un mineur, le représentant légal ou le parent doit créer un compte, puis y ajouter un compte enfant.",
    ],
  },
  {
    title: "Créez votre compte",
    imageUrl: "/images/registrationHelp/personalDataForm.png",
    texts: [
      "Si vous n’avez pas encore de compte FFVB, remplissez le formulaire pour le créer.",
      "Si vous avez déjà un compte, vos données sont récupérées automatiquement une fois connecté avec vos identifiants.",
    ],
  },
  {
    title: "Accédez à l’inscription",
    imageUrl: "/images/registrationHelp/clickOnIconRegistration.png",
    texts: [
      "Cliquez sur l’icône « Inscription ».",
      "Vous pouvez également consulter votre profil.",
    ],
  },
  {
    title: "Le formulaire d’inscription",
    imageUrl: "/images/registrationHelp/registrationForm.png",
    texts: ["Remplissez le formulaire d’inscription."],
  },
  {
    title: "Votre souhait",
    imageUrl: "/images/registrationHelp/competitonFormatSelection.png",
    texts: [
      "Sélectionnez « Pratique hors compétition (extension VPT) » si vous vous entraînez le mercredi soir à Chantaco en championnat BLVB (en général les seniors de plus de 18 ans).",
      "Sélectionnez « Jouer en compétition » si vous vous entraînez le mardi soir ou le samedi matin (en général les jeunes de 12 à 18 ans).",
    ],
  },
  {
    title: "Votre discipline",
    imageUrl: "/images/registrationHelp/gameFormatSelection.png",
    texts: ["Sélectionnez « En salle »."],
  },
  {
    title: "Identifiez votre club",
    imageUrl: "/images/registrationHelp/clubSelection.png",
    texts: [
      "Sélectionnez « 64 - Pyrénées-Atlantiques ».",
      "Sélectionnez le club « VOLLEY-BALL LUZIEN CIBOURIEN ».",
    ],
  },
  {
    title: "Certificat médical",
    imageUrl: "/images/registrationHelp/healthCertificat.png",
    texts: [
      "Si vous êtes un ancien adhérent et que votre certificat médical date de moins de 3 ans, vous n’avez pas besoin d’en fournir un nouveau.",
      "Si vous jouiez en Régionale 2 la saison dernière, demandez à votre médecin un « simple surclassement ».",
    ],
  },
  {
    title: "Souscription d’une assurance",
    imageUrl: "/images/registrationHelp/Insurance.png",
    texts: ["Choisissez de souscrire au contrat collectif « Accident corporel » et sélectionnez l’option de base incluse dans la licence (valorisée à 0,58 € TTC)."],
  },
  {
    title: "Questions légales et RGPD",
    imageUrl: "/images/registrationHelp/legalAndRGPD.png",
    texts: ["Attestez et autorisez selon votre situation et vos souhaits."],
  },
  {
    title: "Votre créneau ou équipe",
    imageUrl: "/images/registrationHelp/teamSelection.png",
    texts: [
      "Pour les jeunes de moins de 18 ans, sélectionnez le créneau correspondant à votre année de naissance.",
      "Pour le loisir, sélectionnez « Senior Loisir BLVB ».",
    ],
  },
  {
    title: "Droit à l’image",
    imageUrl: "/images/registrationHelp/imageRight.png",
    texts: ["Cochez l’une des deux options."],
  },
  {
    title: "Anti-dopage et accident",
    imageUrl: "/images/registrationHelp/control.png",
    texts: ["Validez les autorisations."],
  },
  {
    title: "Informations sur le club",
    imageUrl: "/images/registrationHelp/informationClub.png",
    texts: ["Cliquez sur « Valider » : votre demande d’inscription est envoyée au club."],
  },
];


function RegistrationHelp() {

  const [activeIndex, setActiveIndex] = useState(0);
  const stepTitleRef = useRef(null);

  const step = HELP_STEPS[activeIndex];
  const isFirst = activeIndex === 0;
  const isLast = activeIndex === HELP_STEPS.length - 1;
  const progress = ((activeIndex + 1) / HELP_STEPS.length) * 100;

  //CHANGER D'ÉTAPE PUIS DONNER LE FOCUS AU TITRE (lecteurs d'écran + remonte l'étape à l'écran sur mobile)
  const goToStep = (index) => {
    const nextIndex = Math.min(Math.max(index, 0), HELP_STEPS.length - 1);
    setActiveIndex(nextIndex);
    requestAnimationFrame(() => stepTitleRef.current?.focus());
  };

  return (
    <main className="help">

      {/* EN-TÊTE */}
      <header className="page-hero">
        <div className="wrap page-hero_content">
          <Link to="/registration" className="page-hero_back"><LuArrowLeft aria-hidden="true" /> Inscriptions</Link>
          <p className="page-hero_eyebrow">
            <span className="tricolor" aria-hidden="true"><span></span><span></span><span></span></span>
            Guide d’inscription
          </p>
          <h1 className="page-hero_title">S’inscrire sur le site de la FFVB</h1>
          <p className="page-hero_text">Suivez le guide pas à pas : {HELP_STEPS.length} étapes illustrées pour créer votre compte et demander votre licence au VBLC.</p>
        </div>
      </header>

      <div className="wrap help_layout">

        {/* SOMMAIRE DES ÉTAPES */}
        <nav className="help_toc surface" aria-label="Étapes du guide">
          <p className="help_toc_title">Les étapes</p>
          <ol className="help_toc_list">
            {HELP_STEPS.map((s, index) => (
              <li key={s.title}>
                <button
                  className={`help_toc_item ${index === activeIndex ? "is-active" : ""} ${index < activeIndex ? "is-done" : ""}`}
                  onClick={() => goToStep(index)}
                  aria-current={index === activeIndex ? "step" : undefined}
                >
                  <span className="help_toc_number" aria-hidden="true">
                    {index < activeIndex ? <LuCheck /> : index + 1}
                  </span>
                  {s.title}
                </button>
              </li>
            ))}
          </ol>
        </nav>

        {/* ÉTAPE EN COURS */}
        <section className="help_step surface" aria-labelledby="help-step-title">
          <div className="help_progress">
            <p className="help_progress_label">Étape {activeIndex + 1} sur {HELP_STEPS.length}</p>
            <div className="help_progress_bar" role="progressbar" aria-valuemin={1} aria-valuemax={HELP_STEPS.length} aria-valuenow={activeIndex + 1} aria-label="Progression dans le guide">
              <span style={{ width: `${progress}%` }}></span>
            </div>
          </div>

          <h2 id="help-step-title" className="title-lg help_step_title" ref={stepTitleRef} tabIndex={-1}>{step.title}</h2>

          <figure className="help_screenshot">
            <img src={step.imageUrl} alt={`Capture d’écran du site FFVB : ${step.title}`} />
            <a className="help_screenshot_zoom" href={step.imageUrl} target="_blank" rel="noreferrer">
              <LuMaximize2 aria-hidden="true" /> Agrandir<span className="sr-only"> la capture (nouvel onglet)</span>
            </a>
          </figure>

          <ul className="help_texts">
            {step.texts.map((text) => <li key={text}>{text}</li>)}
          </ul>

          <div className="help_nav">
            <button className="btn btn--secondary" onClick={() => goToStep(activeIndex - 1)} disabled={isFirst}>
              <LuChevronLeft aria-hidden="true" /> Précédent
            </button>
            {isLast
              ? <Link to="/registration" className="btn btn--primary">
                <LuCheck aria-hidden="true" /> Terminer
              </Link>
              : <button className="btn btn--primary" onClick={() => goToStep(activeIndex + 1)}>
                Suivant <LuChevronRight aria-hidden="true" />
              </button>}
          </div>
        </section>
      </div>

      <div className="wrap help_footer">
        <a className="link-arrow" href="https://login.ffvolley.org/" target="_blank" rel="noopener noreferrer">
          Ouvrir le site de la FFVB <LuExternalLink aria-hidden="true" /><span className="sr-only">(nouvel onglet)</span>
        </a>
      </div>
    </main>
  )
};

export default RegistrationHelp;
